/**
 * Social declaration generators — Saudi GOSI / SANED / WPS (legacy CNAS helpers retained).
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  calculateGOSIDeclarationLine,
  calculateGOSIDeclarationSummary,
  getGOSIPaymentDeadline,
  GOSI_DECL_EMPLOYEE_RATE,
  GOSI_DECL_EMPLOYER_RATE,
  GOSI_MAX_BASIC_SAR,
} from '../../pages/salaires/utils/payrollSaudiFormat';
import i18n from '../../i18n';

const dd = (key) => i18n.t(`modules.shared.declarationsDemo.${key}`);

/**
 * Génère le XML CNAS-DAC (Déclaration d'Assiette de Cotisation)
 * Format: XML normalisé pour télédéclaration CNAS
 */
export const generateCNAS_DAC_XML = (declaration, employees) => {
  const { year, month, quarter, periodicity } = declaration;
  const period = periodicity === 'monthly' ? `${year}-${String(month).padStart(2, '0')}` : `${year}-T${quarter}`;
  
  // Calculs CNAS (taux algériens 2024)
  const TAUX_EMPLOYEE = 0.09; // 9% employé
  const TAUX_EMPLOYER = 0.26; // 26% employeur (dont 15% CNAS + autres)
  const TAUX_FNPOS = 0.005; // 0,5% FNPOS
  
  const employeesData = employees.map(emp => {
    const salaireBase = emp.salaireBase || 0;
    const cotisationEmp = salaireBase * TAUX_EMPLOYEE;
    const cotisationEmplr = salaireBase * TAUX_EMPLOYER;
    const fnpos = salaireBase * TAUX_FNPOS;
    
    return {
      matricule: emp.mat || emp.matricule || 'N/A',
      nom: emp.nom || emp.lastName || '',
      prenom: emp.prenom || emp.firstName || '',
      numSecu: emp.securiteSociale || emp.socialSecurityNumber || '',
      dateNaissance: emp.dateNaissance || '',
      salaireBase: salaireBase.toFixed(2),
      cotisationEmploye: cotisationEmp.toFixed(2),
      cotisationEmployeur: cotisationEmplr.toFixed(2),
      fnpos: fnpos.toFixed(2),
      totalCotisation: (cotisationEmp + cotisationEmplr + fnpos).toFixed(2)
    };
  });
  
  const totaux = employeesData.reduce((acc, emp) => ({
    salaires: acc.salaires + parseFloat(emp.salaireBase),
    cotisationsEmp: acc.cotisationsEmp + parseFloat(emp.cotisationEmploye),
    cotisationsEmplr: acc.cotisationsEmplr + parseFloat(emp.cotisationEmployeur),
    fnpos: acc.fnpos + parseFloat(emp.fnpos)
  }), { salaires: 0, cotisationsEmp: 0, cotisationsEmplr: 0, fnpos: 0 });
  
  // XML structure conforme au schéma CNAS
  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += '<DeclarationCNAS_DAC xmlns="http://www.cnas.dz/schemas/dac" version="2.0">\n';
  xml += `  <EnTete>\n`;
  xml += `    <TypeDeclaration>DAC</TypeDeclaration>\n`;
  xml += `    <Periode>${period}</Periode>\n`;
  xml += `    <Periodicite>${periodicity === 'monthly' ? 'monthly' : 'quarterly'}</Periodicite>\n`;
  xml += `    <DateDeclaration>${new Date().toISOString().split('T')[0]}</DateDeclaration>\n`;
  xml += `    <NombreEmployes>${employees.length}</NombreEmployes>\n`;
  xml += `    <Exercice>${year}</Exercice>\n`;
  xml += `  </EnTete>\n`;
  xml += `  <Employeur>\n`;
  xml += `    <NumeroAdherent>${declaration.employerNumber || '0123456789'}</NumeroAdherent>\n`;
  xml += `    <RaisonSociale>${dd('company_name_default')}</RaisonSociale>\n`;
  xml += `    <Activite>${dd('activity_commerce')}</Activite>\n`;
  xml += `    <CodeNAP>47.11.40</CodeNAP>\n`;
  xml += `    <Classe>A</Classe>\n`;
  xml += `    <Statut>SARL</Statut>\n`;
  xml += `    <Adresse>${dd('address_default')}</Adresse>\n`;
  xml += `  </Employeur>\n`;
  xml += `  <Agence>\n`;
  xml += `    <CodeAgence>001</CodeAgence>\n`;
  xml += `    <NomAgence>${dd('agency_name_cnas')}</NomAgence>\n`;
  xml += `    <CompteCCP>000 123 456</CompteCCP>\n`;
  xml += `    <CompteTresor>123-456-789</CompteTresor>\n`;
  xml += `  </Agence>\n`;
  xml += `  <ListeEmployes>\n`;
  
  employeesData.forEach(emp => {
    xml += `    <Employe>\n`;
    xml += `      <Matricule>${emp.matricule}</Matricule>\n`;
    xml += `      <Nom>${emp.nom}</Nom>\n`;
    xml += `      <Prenom>${emp.prenom}</Prenom>\n`;
    xml += `      <NumeroSecuriteSociale>${emp.numSecu}</NumeroSecuriteSociale>\n`;
    xml += `      <DateNaissance>${emp.dateNaissance}</DateNaissance>\n`;
    xml += `      <SalaireBase>${emp.salaireBase}</SalaireBase>\n`;
    xml += `      <CotisationEmploye>${emp.cotisationEmploye}</CotisationEmploye>\n`;
    xml += `      <CotisationEmployeur>${emp.cotisationEmployeur}</CotisationEmployeur>\n`;
    xml += `      <FNPOS>${emp.fnpos}</FNPOS>\n`;
    xml += `      <TotalCotisation>${emp.totalCotisation}</TotalCotisation>\n`;
    xml += `    </Employe>\n`;
  });
  
  xml += `  </ListeEmployes>\n`;
  xml += `  <Totaux>\n`;
  xml += `    <TotalAssietteSalaires>${totaux.salaires.toFixed(2)}</TotalAssietteSalaires>\n`;
  xml += `    <TotalCotisationsEmployes>${totaux.cotisationsEmp.toFixed(2)}</TotalCotisationsEmployes>\n`;
  xml += `    <TotalCotisationsEmployeur>${totaux.cotisationsEmplr.toFixed(2)}</TotalCotisationsEmployeur>\n`;
  xml += `    <TotalFNPOS>${totaux.fnpos.toFixed(2)}</TotalFNPOS>\n`;
  xml += `    <TotalGeneral>${(totaux.cotisationsEmp + totaux.cotisationsEmplr + totaux.fnpos).toFixed(2)}</TotalGeneral>\n`;
  xml += `  </Totaux>\n`;
  xml += '</DeclarationCNAS_DAC>';
  
  return xml;
};

/**
 * Génère le XML DAS (Déclaration Annuelle des Salaires)
 * Format: XML pour déclaration fiscale IRG
 */
export const generateDAS_XML = (declaration, employees) => {
  const { year } = declaration;
  
  const employeesData = employees.map(emp => {
    const salaireNet = emp.salaireNetMensuel || 0;
    const salaireAnnuel = salaireNet * 12;
    // Simulation IRG (barème progressif algérien simplifié)
    const irgAnnuel = calculateIRG(salaireAnnuel);
    
    return {
      matricule: emp.mat || emp.matricule || 'N/A',
      nom: emp.nom || emp.lastName || '',
      prenom: emp.prenom || emp.firstName || '',
      numSecu: emp.securiteSociale || emp.socialSecurityNumber || '',
      dateNaissance: emp.dateNaissance || '',
      salaireNetMensuel: salaireNet.toFixed(2),
      salaireNetAnnuel: salaireAnnuel.toFixed(2),
      irgRetenu: irgAnnuel.toFixed(2)
    };
  });
  
  const totaux = employeesData.reduce((acc, emp) => ({
    salaires: acc.salaires + parseFloat(emp.salaireNetAnnuel),
    irg: acc.irg + parseFloat(emp.irgRetenu)
  }), { salaires: 0, irg: 0 });
  
  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += '<DeclarationDAS xmlns="http://www.mfdgi.gov.dz/schemas/das" version="2.0">\n';
  xml += `  <EnTete>\n`;
  xml += `    <TypeDeclaration>DAS</TypeDeclaration>\n`;
  xml += `    <Exercice>${year}</Exercice>\n`;
  xml += `    <PeriodeReference>01/07/${year - 1} - 30/06/${year}</PeriodeReference>\n`;
  xml += `    <DateDeclaration>${new Date().toISOString().split('T')[0]}</DateDeclaration>\n`;
  xml += `    <NombreEmployes>${employees.length}</NombreEmployes>\n`;
  xml += `  </EnTete>\n`;
  xml += `  <Employeur>\n`;
  xml += `    <NIF>000012345678901</NIF>\n`;
  xml += `    <RaisonSociale>${dd('company_name_default')}</RaisonSociale>\n`;
  xml += `    <Activite>${dd('activity_commerce')}</Activite>\n`;
  xml += `    <CodeNAP>47.11.40</CodeNAP>\n`;
  xml += `    <Statut>SARL</Statut>\n`;
  xml += `    <Adresse>${dd('address_default')}</Adresse>\n`;
  xml += `  </Employeur>\n`;
  xml += `  <ListeSalaries>\n`;
  
  employeesData.forEach(emp => {
    xml += `    <Salarie>\n`;
    xml += `      <Matricule>${emp.matricule}</Matricule>\n`;
    xml += `      <Nom>${emp.nom}</Nom>\n`;
    xml += `      <Prenom>${emp.prenom}</Prenom>\n`;
    xml += `      <NumeroSecuriteSociale>${emp.numSecu}</NumeroSecuriteSociale>\n`;
    xml += `      <DateNaissance>${emp.dateNaissance}</DateNaissance>\n`;
    xml += `      <SalaireNetMensuel>${emp.salaireNetMensuel}</SalaireNetMensuel>\n`;
    xml += `      <SalaireNetAnnuel>${emp.salaireNetAnnuel}</SalaireNetAnnuel>\n`;
    xml += `      <IRG_Retenu>${emp.irgRetenu}</IRG_Retenu>\n`;
    xml += `    </Salarie>\n`;
  });
  
  xml += `  </ListeSalaries>\n`;
  xml += `  <Totaux>\n`;
  xml += `    <TotalSalairesNetsAnnuels>${totaux.salaires.toFixed(2)}</TotalSalairesNetsAnnuels>\n`;
  xml += `    <TotalIRG>${totaux.irg.toFixed(2)}</TotalIRG>\n`;
  xml += `  </Totaux>\n`;
  xml += '</DeclarationDAS>';
  
  return xml;
};

/**
 * Génère le XML CACOBATPH (Congés payés BTP)
 */
export const generateCACOBATH_XML = (declaration, employees) => {
  const { year, month, quarter, periodicity } = declaration;
  const period = periodicity === 'monthly' ? `${year}-${String(month).padStart(2, '0')}` : `${year}-T${quarter}`;
  
  // Taux CACOBATPH
  const TAUX_CONGES = 0.1221; // 12,21% congés payés
  const TAUX_INTEMPERIES = 0.0075; // 0,75% chômage-intempéries
  
  const employeesData = employees.map(emp => {
    const salaireBase = emp.salaireBase || 0;
    const congesPaies = salaireBase * TAUX_CONGES;
    const intemperies = salaireBase * TAUX_INTEMPERIES;
    
    return {
      matricule: emp.mat || emp.matricule || 'N/A',
      nom: emp.nom || emp.lastName || '',
      prenom: emp.prenom || emp.firstName || '',
      numSecu: emp.securiteSociale || emp.socialSecurityNumber || '',
      dateNaissance: emp.dateNaissance || '',
      salaireBase: salaireBase.toFixed(2),
      congesPaies: congesPaies.toFixed(2),
      intemperies: intemperies.toFixed(2),
      total: (congesPaies + intemperies).toFixed(2)
    };
  });
  
  const totaux = employeesData.reduce((acc, emp) => ({
    salaires: acc.salaires + parseFloat(emp.salaireBase),
    conges: acc.conges + parseFloat(emp.congesPaies),
    intemperies: acc.intemperies + parseFloat(emp.intemperies)
  }), { salaires: 0, conges: 0, intemperies: 0 });
  
  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += '<DeclarationCACOBATH xmlns="http://www.cacobatph.dz/schemas/declaration" version="2.0">\n';
  xml += `  <EnTete>\n`;
  xml += `    <TypeDeclaration>CACOBATPH</TypeDeclaration>\n`;
  xml += `    <Periode>${period}</Periode>\n`;
  xml += `    <Periodicite>${periodicity === 'monthly' ? 'monthly' : 'quarterly'}</Periodicite>\n`;
  xml += `    <DateDeclaration>${new Date().toISOString().split('T')[0]}</DateDeclaration>\n`;
  xml += `    <NombreEmployes>${employees.length}</NombreEmployes>\n`;
  xml += `    <Exercice>${year}</Exercice>\n`;
  xml += `  </EnTete>\n`;
  xml += `  <Employeur>\n`;
  xml += `    <NumeroAffiliation>CACO-0123456</NumeroAffiliation>\n`;
  xml += `    <RaisonSociale>${dd('company_name_btp')}</RaisonSociale>\n`;
  xml += `    <Activite>${dd('activity_btp')}</Activite>\n`;
  xml += `    <CodeNAP>41.20.00</CodeNAP>\n`;
  xml += `    <Secteur>BTP</Secteur>\n`;
  xml += `    <Statut>SARL</Statut>\n`;
  xml += `    <Adresse>${dd('address_default')}</Adresse>\n`;
  xml += `  </Employeur>\n`;
  xml += `  <Agence>\n`;
  xml += `    <CodeAgence>001</CodeAgence>\n`;
  xml += `    <NomAgence>${dd('agency_name_cacobatph')}</NomAgence>\n`;
  xml += `    <CompteCCP>000 789 012</CompteCCP>\n`;
  xml += `  </Agence>\n`;
  xml += `  <ListeEmployes>\n`;
  
  employeesData.forEach(emp => {
    xml += `    <Employe>\n`;
    xml += `      <Matricule>${emp.matricule}</Matricule>\n`;
    xml += `      <Nom>${emp.nom}</Nom>\n`;
    xml += `      <Prenom>${emp.prenom}</Prenom>\n`;
    xml += `      <NumeroSecuriteSociale>${emp.numSecu}</NumeroSecuriteSociale>\n`;
    xml += `      <DateNaissance>${emp.dateNaissance}</DateNaissance>\n`;
    xml += `      <SalaireBase>${emp.salaireBase}</SalaireBase>\n`;
    xml += `      <CongesPayes>${emp.congesPaies}</CongesPayes>\n`;
    xml += `      <ChomageIntemperies>${emp.intemperies}</ChomageIntemperies>\n`;
    xml += `      <TotalCotisation>${emp.total}</TotalCotisation>\n`;
    xml += `    </Employe>\n`;
  });
  
  xml += `  </ListeEmployes>\n`;
  xml += `  <Totaux>\n`;
  xml += `    <TotalAssietteSalaires>${totaux.salaires.toFixed(2)}</TotalAssietteSalaires>\n`;
  xml += `    <TotalCongesPayes>${totaux.conges.toFixed(2)}</TotalCongesPayes>\n`;
  xml += `    <TotalChomageIntemperies>${totaux.intemperies.toFixed(2)}</TotalChomageIntemperies>\n`;
  xml += `    <TotalGeneral>${(totaux.conges + totaux.intemperies).toFixed(2)}</TotalGeneral>\n`;
  xml += `  </Totaux>\n`;
  xml += '</DeclarationCACOBATH>';
  
  return xml;
};

// ---------------------------------------------------------------------------
// Saudi GOSI / SANED / WPS generators
// ---------------------------------------------------------------------------

const escapeXml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const getEmployerBlock = (declaration) => ({
  companyNameAr: declaration.companyNameAr || 'شركة قواعد للبرمجيات',
  companyNameEn: declaration.companyNameEn || 'Qawaem Software Company',
  gosiEstablishmentNumber: declaration.gosiEstablishmentNumber || declaration.numeroEmployeur || '',
  unifiedNumber: declaration.unifiedNumber || '',
  commercialRegistration: declaration.commercialRegistration || '',
});

const mapEmployeeIdentity = (emp) => ({
  nationalId: emp.nationalId || emp.cin || emp.iqamaNumber || emp.numeroIdentite || '',
  nameAr: emp.nomAr || `${emp.prenom || emp.firstName || ''} ${emp.nom || emp.lastName || ''}`.trim(),
  gosiNumber: emp.gosiNumber || emp.socialSecurityNumber || emp.securiteSociale || '',
  matricule: emp.mat || emp.matricule || '',
});

/** Monthly GOSI declaration XML (portal upload format — future integration). */
export const generateGOSI_Monthly_XML = (declaration, employees) => {
  const { year, month } = declaration;
  const period = `${year}-${String(month).padStart(2, '0')}`;
  const employer = getEmployerBlock(declaration);
  const summary = calculateGOSIDeclarationSummary(employees);
  const deadline = getGOSIPaymentDeadline(year, month);

  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += '<GOSIMonthlyDeclaration xmlns="https://www.gosi.gov.sa/schemas/monthly" version="1.0">\n';
  xml += '  <Header>\n';
  xml += '    <DeclarationType>GOSI_MONTHLY</DeclarationType>\n';
  xml += `    <Period>${period}</Period>\n`;
  xml += '    <Periodicity>monthly</Periodicity>\n';
  xml += `    <DeclarationDate>${new Date().toISOString().split('T')[0]}</DeclarationDate>\n`;
  xml += `    <PaymentDeadline>${deadline.toISOString().split('T')[0]}</PaymentDeadline>\n`;
  xml += `    <Currency>SAR</Currency>\n`;
  xml += `    <TotalEmployees>${summary.totalRegistered}</TotalEmployees>\n`;
  xml += `    <SaudiNationalsCount>${summary.saudiCount}</SaudiNationalsCount>\n`;
  xml += `    <ExpatsCount>${summary.expatCount}</ExpatsCount>\n`;
  xml += '  </Header>\n';
  xml += '  <Employer>\n';
  xml += `    <CompanyNameArabic>${escapeXml(employer.companyNameAr)}</CompanyNameArabic>\n`;
  xml += `    <CompanyNameEnglish>${escapeXml(employer.companyNameEn)}</CompanyNameEnglish>\n`;
  xml += `    <GOSIEstablishmentNumber>${escapeXml(employer.gosiEstablishmentNumber)}</GOSIEstablishmentNumber>\n`;
  xml += `    <UnifiedNumber>${escapeXml(employer.unifiedNumber)}</UnifiedNumber>\n`;
  xml += `    <CommercialRegistration>${escapeXml(employer.commercialRegistration)}</CommercialRegistration>\n`;
  xml += '  </Employer>\n';
  xml += '  <Employees>\n';

  summary.lines.forEach(({ employee, basic, allowances, employeeContrib, employerContrib, pensionEmployee, sanedEmployee, isContributor, highRisk }) => {
    const id = mapEmployeeIdentity(employee);
    xml += '    <Employee>\n';
    xml += `      <NationalId>${escapeXml(id.nationalId)}</NationalId>\n`;
    xml += `      <NameArabic>${escapeXml(id.nameAr)}</NameArabic>\n`;
    xml += `      <GOSINumber>${escapeXml(id.gosiNumber)}</GOSINumber>\n`;
    xml += `      <BasicSalary>${basic.toFixed(2)}</BasicSalary>\n`;
    xml += `      <Allowances>${allowances.toFixed(2)}</Allowances>\n`;
    xml += `      <ContributoryCap>${GOSI_MAX_BASIC_SAR.toFixed(2)}</ContributoryCap>\n`;
    xml += `      <EmployeeContribution rate="${GOSI_DECL_EMPLOYEE_RATE}">${employeeContrib.toFixed(2)}</EmployeeContribution>\n`;
    xml += `      <EmployerContribution rate="${GOSI_DECL_EMPLOYER_RATE}">${employerContrib.toFixed(2)}</EmployerContribution>\n`;
    xml += `      <PensionEmployee>${pensionEmployee.toFixed(2)}</PensionEmployee>\n`;
    xml += `      <SANEDEmployee>${sanedEmployee.toFixed(2)}</SANEDEmployee>\n`;
    xml += `      <IsContributor>${isContributor}</IsContributor>\n`;
    xml += `      <HighRiskJob>${highRisk}</HighRiskJob>\n`;
    xml += '    </Employee>\n';
  });

  xml += '  </Employees>\n';
  xml += '  <Totals>\n';
  xml += `    <TotalBasicSalaries>${summary.totalBasicSalaries.toFixed(2)}</TotalBasicSalaries>\n`;
  xml += `    <TotalEmployeeContributions>${summary.totalEmployeeContrib.toFixed(2)}</TotalEmployeeContributions>\n`;
  xml += `    <TotalEmployerContributions>${summary.totalEmployerContrib.toFixed(2)}</TotalEmployerContributions>\n`;
  xml += `    <GrandTotal>${summary.totalContributions.toFixed(2)}</GrandTotal>\n`;
  xml += '  </Totals>\n';
  xml += '</GOSIMonthlyDeclaration>';
  return xml;
};

/** SANED unemployment insurance report (0.75% employee — part of GOSI bundle). */
export const generateSANED_Monthly_XML = (declaration, employees) => {
  const { year, month } = declaration;
  const period = `${year}-${String(month).padStart(2, '0')}`;
  const employer = getEmployerBlock(declaration);

  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += '<SANEDMonthlyReport xmlns="https://www.gosi.gov.sa/schemas/saned" version="1.0">\n';
  xml += `  <Period>${period}</Period>\n`;
  xml += `  <GOSIEstablishmentNumber>${escapeXml(employer.gosiEstablishmentNumber)}</GOSIEstablishmentNumber>\n`;
  xml += `  <Coverage>${escapeXml(dd('saned_coverage'))}</Coverage>\n`;
  xml += '  <Employees>\n';

  employees.filter((emp) => calculateGOSIDeclarationLine(emp).isSaudi).forEach((emp) => {
    const line = calculateGOSIDeclarationLine(emp);
    const id = mapEmployeeIdentity(emp);
    xml += '    <Employee>\n';
    xml += `      <NationalId>${escapeXml(id.nationalId)}</NationalId>\n`;
    xml += `      <NameArabic>${escapeXml(id.nameAr)}</NameArabic>\n`;
    xml += `      <BasicSalary>${line.basic.toFixed(2)}</BasicSalary>\n`;
    xml += `      <SANEDEmployeeContribution>${line.sanedEmployee.toFixed(2)}</SANEDEmployeeContribution>\n`;
    xml += '    </Employee>\n';
  });

  xml += '  </Employees>\n';
  xml += '</SANEDMonthlyReport>';
  return xml;
};

/** WPS (Wage Protection System) monthly report XML. */
export const generateWPS_Report_XML = (declaration, employees) => {
  const { year, month } = declaration;
  const period = `${year}-${String(month).padStart(2, '0')}`;
  const employer = getEmployerBlock(declaration);

  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += '<WPSMonthlyReport xmlns="https://mcs.gov.sa/schemas/wps" version="1.0">\n';
  xml += `  <Period>${period}</Period>\n`;
  xml += `  <Currency>SAR</Currency>\n`;
  xml += `  <UnifiedNumber>${escapeXml(employer.unifiedNumber)}</UnifiedNumber>\n`;
  xml += `  <CommercialRegistration>${escapeXml(employer.commercialRegistration)}</CommercialRegistration>\n`;
  xml += '  <Employees>\n';

  employees.forEach((emp) => {
    const basic = calculateGOSIDeclarationLine(emp).basic;
    const allowances = calculateGOSIDeclarationLine(emp).allowances;
    const id = mapEmployeeIdentity(emp);
    const net = basic + allowances - calculateGOSIDeclarationLine(emp).employeeContrib;
    xml += '    <Employee>\n';
    xml += `      <NationalId>${escapeXml(id.nationalId)}</NationalId>\n`;
    xml += `      <Name>${escapeXml(id.nameAr)}</Name>\n`;
    xml += `      <IBAN>${escapeXml(emp.iban || emp.bankIban || '')}</IBAN>\n`;
    xml += `      <BasicSalary>${basic.toFixed(2)}</BasicSalary>\n`;
    xml += `      <Allowances>${allowances.toFixed(2)}</Allowances>\n`;
    xml += `      <NetTransferAmount>${net.toFixed(2)}</NetTransferAmount>\n`;
    xml += '    </Employee>\n';
  });

  xml += '  </Employees>\n';
  xml += '</WPSMonthlyReport>';
  return xml;
};

const SAUDI_DECL_TYPES = new Set(['gosi_monthly', 'saned_monthly', 'wps_report']);

const formatSar = (amount) =>
  Number(amount || 0).toLocaleString('ar-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Saudi GOSI / SANED / WPS PDF report (Arabic labels — GOSI / WPS context). */
export const generateSaudiDeclarationPDF = (declaration, employees, type) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const employer = getEmployerBlock(declaration);
  const summary = calculateGOSIDeclarationSummary(employees);
  const deadline = declaration.month
    ? getGOSIPaymentDeadline(declaration.year, declaration.month)
    : null;

  const titleKeys = {
    gosi_monthly: ['pdf_title_gosi', 'pdf_org_gosi'],
    saned_monthly: ['pdf_title_saned', 'pdf_org_saned'],
    wps_report: ['pdf_title_wps', 'pdf_org_wps'],
  };
  const [titleKey, orgKey] = titleKeys[type] || titleKeys.gosi_monthly;

  let currentY = 15;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(dd(titleKey), pageWidth / 2, currentY, { align: 'center' });
  currentY += 8;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(dd(orgKey), pageWidth / 2, currentY, { align: 'center' });
  currentY += 12;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(dd('pdf_employer_section'), 15, currentY);
  currentY += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  [
    `${dd('pdf_company_ar')}: ${employer.companyNameAr}`,
    `${dd('pdf_company_en')}: ${employer.companyNameEn}`,
    `${dd('pdf_gosi_establishment')}: ${employer.gosiEstablishmentNumber || '—'}`,
    `${dd('pdf_unified_number')}: ${employer.unifiedNumber || '—'}`,
    `${dd('pdf_cr')}: ${employer.commercialRegistration || '—'}`,
  ].forEach((line) => {
    doc.text(line, 15, currentY);
    currentY += 5;
  });

  currentY += 4;
  const periodLabel = declaration.month
    ? new Date(declaration.year, declaration.month - 1, 1).toLocaleDateString('ar-SA', {
        month: 'long',
        year: 'numeric',
      })
    : String(declaration.year);
  doc.text(`${dd('pdf_period')}: ${periodLabel}`, 15, currentY);
  doc.text(dd('pdf_currency_sar'), pageWidth / 2, currentY);
  currentY += 5;
  if (deadline) {
    doc.text(
      `${dd('pdf_payment_deadline')}: ${deadline.toLocaleDateString('ar-SA')} ${dd('pdf_payment_deadline_suffix')}`,
      15,
      currentY
    );
    currentY += 5;
  }

  if (type === 'gosi_monthly') {
    doc.text(`${dd('pdf_registered')}: ${summary.totalRegistered}`, 15, currentY);
    doc.text(`${dd('pdf_saudis')}: ${summary.saudiCount}`, pageWidth / 2 - 20, currentY);
    doc.text(`${dd('pdf_expats')}: ${summary.expatCount}`, pageWidth - 55, currentY);
    currentY += 8;
  }

  const columns =
    type === 'wps_report'
      ? [dd('col_id_iqama'), dd('col_name'), dd('col_basic_sar'), dd('col_allowances'), dd('col_net_wps')]
      : [dd('col_id_iqama'), dd('col_gosi_number'), dd('col_basic_sar'), dd('col_employee_rate'), dd('col_employer_sar'), dd('col_total_sar')];

  const tableData = employees.map((emp) => {
    const line = calculateGOSIDeclarationLine(emp);
    const id = mapEmployeeIdentity(emp);
    if (type === 'wps_report') {
      const net = line.basic + line.allowances - line.employeeContrib;
      return [id.nationalId || '—', id.nameAr, formatSar(line.basic), formatSar(line.allowances), formatSar(net)];
    }
    return [
      id.nationalId || '—',
      id.gosiNumber || '—',
      formatSar(line.basic),
      line.isContributor ? `${GOSI_DECL_EMPLOYEE_RATE}%` : '0%',
      formatSar(line.employerContrib),
      formatSar(line.totalContrib),
    ];
  });

  autoTable(doc, {
    head: [columns],
    body: tableData,
    startY: currentY,
    theme: 'grid',
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [0, 108, 53], textColor: 255, fontStyle: 'bold' },
    margin: { left: 10, right: 10 },
  });

  currentY = doc.lastAutoTable.finalY + 10;

  if (type === 'gosi_monthly') {
    doc.setFont('helvetica', 'bold');
    doc.text(dd('pdf_totals'), 15, currentY);
    currentY += 6;
    doc.setFont('helvetica', 'normal');
    [
      [dd('pdf_total_basic_salaries'), summary.totalBasicSalaries],
      [dd('pdf_total_employee_contrib'), summary.totalEmployeeContrib],
      [dd('pdf_total_employer_contrib'), summary.totalEmployerContrib],
      [dd('pdf_grand_total'), summary.totalContributions],
    ].forEach(([label, value]) => {
      doc.text(`${label}: ${formatSar(value)} ${dd('currency_sar')}`, 15, currentY);
      currentY += 5;
    });
  }

  doc.setFontSize(7);
  doc.setTextColor(120, 120, 120);
  doc.text(dd('gosi_portal_footer'), 15, pageHeight - 10);
  doc.setTextColor(0, 0, 0);

  return doc;
};

/** Route PDF generation — Saudi types use GOSI layout; legacy types use Arabic Saudi labels. */
export const generateDeclarationPDF = (declaration, employees, type) => {
  if (SAUDI_DECL_TYPES.has(type)) {
    return generateSaudiDeclarationPDF(declaration, employees, type);
  }
  return generateLegacyDeclarationPDF(declaration, employees, type);
};

const generateLegacyDeclarationPDF = (declaration, employees, type) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const employeurInfo = {
    raisonSociale: dd('company_name_default'),
    activite: dd('activity_commerce'),
    adresse: dd('address_default'),
    numeroAdherent: type === 'cnas_dac' || type === 'cacobatph' ? '0123456789' : null,
    nif: type === 'das' ? '300000000000003' : null,
    nap: '47.11.40',
    classe: 'A',
    statut: 'ذ.م.م',
    agenceCode: '001',
    agenceNom: dd('agency_name_cnas'),
    agenceAdresse: dd('agency_address'),
    compteIBAN: 'SA00 0000 0000 0000 0000 0000',
    telephoneAgence: '+966 11 000 0000',
  };

  let currentY = 15;

  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  const title = getDeclarationTitle(type);
  doc.text(title, pageWidth / 2, currentY, { align: 'center' });
  currentY += 10;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.text(dd('pdf_kingdom_header'), pageWidth / 2, currentY, { align: 'center' });
  currentY += 5;
  doc.text(getOrganism(type), pageWidth / 2, currentY, { align: 'center' });
  currentY += 10;

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.5);
  doc.rect(10, currentY, pageWidth - 20, 75);
  currentY += 5;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(dd('pdf_employer_info'), 15, currentY);
  currentY += 7;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`${dd('pdf_company_name')}: ${employeurInfo.raisonSociale}`, 15, currentY);
  currentY += 5;
  doc.text(`${dd('pdf_activity')}: ${employeurInfo.activite}`, 15, currentY);
  doc.text(`${dd('pdf_nap')}: ${employeurInfo.nap}`, pageWidth - 60, currentY);
  currentY += 5;
  doc.text(`${dd('label_address')}: ${employeurInfo.adresse}`, 15, currentY);
  currentY += 5;

  if (type === 'das') {
    doc.text(`${dd('pdf_nif')}: ${employeurInfo.nif}`, 15, currentY);
  } else {
    doc.text(`${dd('pdf_member_number')}: ${employeurInfo.numeroAdherent}`, 15, currentY);
    doc.text(`${dd('pdf_class')}: ${employeurInfo.classe}`, pageWidth / 2, currentY);
    doc.text(`${dd('pdf_status')}: ${employeurInfo.statut}`, pageWidth - 50, currentY);
  }
  currentY += 7;

  doc.setFont('helvetica', 'bold');
  doc.text(dd('pdf_declaration_period'), 15, currentY);
  currentY += 6;

  doc.setFont('helvetica', 'normal');
  const periodText = getPeriodText(declaration, type);
  doc.text(periodText, 15, currentY);
  doc.text(`${dd('pdf_fiscal_year')}: ${declaration.year}`, pageWidth / 2, currentY);
  currentY += 5;
  doc.text(`${dd('pdf_declaration_date')}: ${new Date().toLocaleDateString('ar-SA')}`, 15, currentY);
  doc.text(`${dd('pdf_employee_count')}: ${employees.length}`, pageWidth / 2, currentY);
  currentY += 7;

  if (type !== 'das') {
    doc.setFont('helvetica', 'bold');
    doc.text(dd('pdf_agency_section_gosi'), 15, currentY);
    currentY += 6;

    doc.setFont('helvetica', 'normal');
    doc.text(`${dd('pdf_agency_name')}: ${employeurInfo.agenceNom}`, 15, currentY);
    doc.text(`${dd('pdf_agency_code')}: ${employeurInfo.agenceCode}`, pageWidth - 60, currentY);
    currentY += 5;
    doc.text(`${dd('label_address')}: ${employeurInfo.agenceAdresse}`, 15, currentY);
    currentY += 5;
    doc.text(`${dd('pdf_bank_account')}: ${employeurInfo.compteIBAN}`, 15, currentY);
  }

  currentY += 10;

  if (type !== 'das') {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(dd('pdf_staff_movement'), 15, currentY);
    currentY += 6;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const nbPrecedent = employees.length;
    const entrees = 0;
    const sorties = 0;
    doc.text(`${dd('pdf_prev_insured')}: ${nbPrecedent}`, 15, currentY);
    currentY += 5;
    doc.text(`${dd('pdf_entries')}: ${entrees}`, 15, currentY);
    doc.text(`${dd('pdf_exits')}: ${sorties}`, pageWidth / 2, currentY);
    currentY += 10;
  }

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(dd('pdf_employee_list'), pageWidth / 2, currentY, { align: 'center' });
  currentY += 7;

  const tableData = employees.map(emp => getEmployeeRowData(emp, type));
  const columns = getTableColumns(type);

  autoTable(doc, {
    head: [columns],
    body: tableData,
    startY: currentY,
    theme: 'grid',
    styles: {
      fontSize: 7,
      cellPadding: 1.5,
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [0, 108, 53],
      textColor: 255,
      fontStyle: 'bold',
      halign: 'center',
    },
    columnStyles: {
      0: { cellWidth: 20 },
      1: { cellWidth: 30 },
      2: { cellWidth: 30 },
    },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    margin: { left: 10, right: 10 },
  });

  currentY = doc.lastAutoTable.finalY + 10;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(dd('pdf_financial_summary'), 15, currentY);
  currentY += 7;

  const totaux = calculateTotals(employees, type);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  Object.entries(totaux).forEach(([key, value]) => {
    doc.text(`${dd(key)}`, 20, currentY);
    doc.text(`: ${formatSar(value)} ${dd('currency_sar')}`, pageWidth - 70, currentY);
    currentY += 5;
  });

  if (type !== 'das') {
    currentY += 3;
    doc.setFont('helvetica', 'bold');
    const assietteTotal = totaux.total_assiette_salaires || 0;
    doc.text(dd('pdf_total_contribution_base'), 20, currentY);
    doc.text(`: ${formatSar(assietteTotal)} ${dd('currency_sar')}`, pageWidth - 70, currentY);
    currentY += 7;
  }

  if (type !== 'das' && currentY < pageHeight - 40) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`${dd('pdf_amount_paid')}: _________________ ${dd('currency_sar')}`, 15, currentY);
    currentY += 5;
    doc.text(`${dd('pdf_deduct')}: _________________ ${dd('currency_sar')}`, 15, currentY);
    currentY += 5;
    doc.text(dd('pdf_payment_method'), 15, currentY);
    currentY += 5;
    doc.text(`${dd('pdf_check_number')}: _______________________`, 15, currentY);
    currentY += 10;
  }

  if (currentY > pageHeight - 35) {
    doc.addPage();
    currentY = 20;
  }

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(dd('pdf_certification'), 15, currentY);
  currentY += 10;

  doc.setFont('helvetica', 'normal');
  doc.text(`${dd('pdf_done_at')}: _____________________`, 15, currentY);
  doc.text(dd('pdf_date_line'), pageWidth / 2, currentY);
  currentY += 15;

  doc.text(dd('pdf_stamp_signature'), pageWidth - 70, currentY);
  doc.rect(pageWidth - 75, currentY + 3, 60, 30);

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 100, 100);

    const footerY = pageHeight - 8;
    doc.text(dd('pdf_page_of', { current: i, total: pageCount }), pageWidth / 2, footerY, { align: 'center' });
    doc.text(`${title} - ${declaration.year}`, 15, footerY);
    doc.text('Qawaem', pageWidth - 20, footerY);

    doc.setTextColor(0, 0, 0);
  }

  return doc;
};

// Helpers
const calculateIRG = (salaireAnnuel) => {
  // Barème IRG algérien 2024 (simplifié)
  if (salaireAnnuel <= 240000) return 0;
  if (salaireAnnuel <= 480000) return (salaireAnnuel - 240000) * 0.20;
  if (salaireAnnuel <= 960000) return 48000 + (salaireAnnuel - 480000) * 0.30;
  return 192000 + (salaireAnnuel - 960000) * 0.35;
};

const getDeclarationTitle = (type) => {
  switch (type) {
    case 'cnas_dac': return dd('pdf_title_cnas_dac');
    case 'das': return dd('pdf_title_das');
    case 'cacobatph': return dd('pdf_title_cacobatph');
    default: return dd('pdf_title_default');
  }
};

const getOrganism = (type) => {
  switch (type) {
    case 'cnas_dac': return dd('pdf_org_gosi');
    case 'das': return dd('pdf_org_zatca');
    case 'cacobatph': return dd('pdf_org_gosi');
    default: return dd('pdf_org_social_default');
  }
};

const getPeriodText = (declaration, type) => {
  const { year, month, quarter, periodicity } = declaration;
  if (type === 'das') return `${dd('pdf_fiscal_year')}: ${year}`;
  if (periodicity === 'monthly') {
    const label = new Date(year, month - 1, 1).toLocaleDateString('ar-SA', { month: 'long', year: 'numeric' });
    return `${dd('pdf_period')}: ${label}`;
  }
  return dd('pdf_quarter', { quarter, year });
};

const getTableColumns = (type) => {
  const base = [dd('col_matricule'), dd('col_last_name'), dd('col_first_name'), dd('col_ss_number')];
  switch (type) {
    case 'cnas_dac':
      return [...base, dd('col_base_salary_sar'), dd('col_employee_contrib_sar'), dd('col_employer_contrib_sar'), dd('col_total_contrib_sar')];
    case 'das':
      return [...base, dd('col_net_monthly_sar'), dd('col_net_annual_sar'), dd('col_withholding_tax_sar')];
    case 'cacobatph':
      return [...base, dd('col_base_salary_sar'), dd('col_paid_leave_sar'), dd('col_weather_unemployment_sar'), dd('col_total_contrib_sar')];
    default:
      return base;
  }
};

const getEmployeeRowData = (emp, type) => {
  const base = [
    emp.mat || emp.matricule || 'N/A',
    emp.nom || emp.lastName || '',
    emp.prenom || emp.firstName || '',
    emp.securiteSociale || emp.socialSecurityNumber || 'N/A'
  ];
  
  const salaireBase = emp.salaireBase || 0;
  const salaireNet = emp.salaireNetMensuel || 0;
  
  switch (type) {
    case 'cnas_dac':
      const cotisEmp = salaireBase * 0.09;
      const cotisEmplr = salaireBase * 0.26;
      return [...base, salaireBase.toFixed(2), cotisEmp.toFixed(2), cotisEmplr.toFixed(2), (cotisEmp + cotisEmplr).toFixed(2)];
    case 'das':
      const salaireAnnuel = salaireNet * 12;
      const irg = calculateIRG(salaireAnnuel);
      return [...base, salaireNet.toFixed(2), salaireAnnuel.toFixed(2), irg.toFixed(2)];
    case 'cacobatph':
      const conges = salaireBase * 0.1221;
      const intemp = salaireBase * 0.0075;
      return [...base, salaireBase.toFixed(2), conges.toFixed(2), intemp.toFixed(2), (conges + intemp).toFixed(2)];
    default:
      return base;
  }
};

const calculateTotals = (employees, type) => {
  const totaux = {};

  employees.forEach(emp => {
    const salaireBase = emp.salaireBase || 0;
    const salaireNet = emp.salaireNetMensuel || 0;

    switch (type) {
      case 'cnas_dac':
        totaux.total_assiette_salaires = (totaux.total_assiette_salaires || 0) + salaireBase;
        totaux.total_cotisations_employees = (totaux.total_cotisations_employees || 0) + (salaireBase * 0.09);
        totaux.total_cotisations_employer = (totaux.total_cotisations_employer || 0) + (salaireBase * 0.26);
        totaux.total_fnpos = (totaux.total_fnpos || 0) + (salaireBase * 0.005);
        totaux.total_general_cotisations = (totaux.total_general_cotisations || 0) + (salaireBase * 0.35);
        break;
      case 'das':
        totaux.total_salaires_nets_annuels = (totaux.total_salaires_nets_annuels || 0) + (salaireNet * 12);
        totaux.total_withholding_tax = (totaux.total_withholding_tax || 0) + calculateIRG(salaireNet * 12);
        break;
      case 'cacobatph':
        totaux.total_assiette_salaires = (totaux.total_assiette_salaires || 0) + salaireBase;
        totaux.total_conges_payes = (totaux.total_conges_payes || 0) + (salaireBase * 0.1221);
        totaux.total_chomage_intemperies = (totaux.total_chomage_intemperies || 0) + (salaireBase * 0.0075);
        totaux.total_general_cotisations = (totaux.total_general_cotisations || 0) + (salaireBase * 0.1296);
        break;
      default:
        break;
    }
  });

  return totaux;
};

/**
 * Télécharge un fichier (XML ou PDF)
 */
export const downloadFile = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

